import { use, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getTestProps } from '../../../lib/helpers';

import LinkButton from '../../../components/LinkButton/LinkButton';

import useSelectedSpace from '../../../hooks/useSelectedSpace';

import UserContext from '../../../contexts/UserContext';

interface DashboardIntroProps {
  testId?: string;
}

const DashboardIntro = ({ testId }: DashboardIntroProps) => {
  const { t } = useTranslation();
  const { userData, isAdmin } = use(UserContext);
  const { space, spaceLimits, buildUrlWithSpace } = useSelectedSpace();

  const description = useMemo(() => {
    if (space) {
      return t('Global.WelcomeDashboard');
    }
    return isAdmin ? (
      <Trans i18nKey={'Global.WelcomeDashboardNoSpaceAdmin'} />
    ) : (
      <Trans i18nKey={'Global.WelcomeDashboardNoSpace'} />
    );
  }, [space, t, isAdmin]);

  const heading = useMemo(() => {
    if (!userData?.firstName && !userData?.lastName) return t('Global.Hi');
    return `${t('Global.Hi')}, ${userData?.firstName || ''} ${
      userData?.lastName || ''
    }`;
  }, [userData?.firstName, userData?.lastName, t]);

  return (
    <section className="flex items-center gap-8 gap-md-12">
      <div className="w-full flex flex-col xl:flex-row xl:items-end xl:justify-between gap-8">
        <div className="dark:text-white">
          <h2 className="text-3xl" {...getTestProps(testId, 'heading')}>
            {heading}
          </h2>
          <p className="mt-3 text-lg" {...getTestProps(testId, 'description')}>
            {t('Global.WelcomeToFlotiq')} {description}
          </p>
        </div>
        {!!space && (
          <div className="flex flex-wrap md:flex-nowrap xl:justify-end gap-2 lg:pt-1">
            <LinkButton
              buttonColor="blueBordered"
              link={`/space/edit/${space}`}
              {...getTestProps(testId, 'space-details', 'testId')}
            >
              {t('Spaces.SpaceDetails')}
            </LinkButton>
            <LinkButton
              link={buildUrlWithSpace('space-settings')}
              buttonColor="blueBordered"
              {...getTestProps(testId, 'space-settings', 'testId')}
            >
              {t('Global.SpaceSettings')}
            </LinkButton>
            {spaceLimits?.price !== -1 && (
              <LinkButton
                link={`/space/upgrade/${space}`}
                {...getTestProps(testId, 'upgrade-plan', 'testId')}
              >
                {t('Global.UpgradePlan')}
              </LinkButton>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default DashboardIntro;
